import React, { useEffect, useState } from 'react';
import useBackButtonClose from '../utils/useBackButtonClose';

export default function ConfirmDialog({
  open,
  title = '확인',
  message,
  confirmLabel = '확인',
  cancelLabel = '취소',
  danger = false,
  onConfirm,
  onCancel,
  // When set, user must type this exact string to enable the confirm button.
  // Used for high-risk irreversible actions (e.g. account deletion).
  requireTypedConfirmation = null,
  // Optional extra slot rendered between message and the typed-confirmation
  // input. Use for opt-in checkboxes (e.g. cascade-delete options).
  children = null,
}) {
  const [typed, setTyped] = useState('');
  const needsTyping = !!requireTypedConfirmation;
  const canConfirm = !needsTyping || typed.trim() === String(requireTypedConfirmation).trim();

  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
      if (e.key === 'Enter' && canConfirm) onConfirm?.();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onConfirm, onCancel, canConfirm]);

  // 모바일 뒤로가기로 다이얼로그만 닫기(SPA 이탈 방지). 닫기 = 취소.
  useBackButtonClose(open, () => onCancel?.());

  if (!open) return null;

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          background: 'var(--surface)', borderRadius: 'var(--radius)',
          border: '1px solid var(--border)',
          padding: 24, maxWidth: 440, width: '100%',
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
        }}
      >
        <h3 style={{ margin: '0 0 12px', fontSize: '1.05rem' }}>{title}</h3>
        <div style={{
          color: 'var(--text-muted)', fontSize: '0.92rem',
          lineHeight: 1.55, whiteSpace: 'pre-line',
          marginBottom: (needsTyping || children) ? 12 : 24,
        }}>
          {message}
        </div>
        {children && (
          <div style={{ marginBottom: needsTyping ? 12 : 20 }}>
            {children}
          </div>
        )}
        {needsTyping && (
          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: '0.85rem', color: 'var(--text)', margin: '0 0 6px' }}>
              확인을 위해 <strong style={{ color: '#c62828' }}>{requireTypedConfirmation}</strong> 을(를) 입력해주세요:
            </p>
            <input
              className="input"
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              placeholder={String(requireTypedConfirmation)}
              style={{ width: '100%' }}
            />
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            className="btn btn-outline"
            onClick={onCancel}
            style={{ padding: '8px 18px', fontSize: '0.9rem' }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={canConfirm ? onConfirm : undefined}
            disabled={!canConfirm}
            autoFocus={!needsTyping}
            style={{
              padding: '8px 18px', fontSize: '0.9rem',
              background: !canConfirm ? '#ccc' : (danger ? '#ff5252' : undefined),
              borderColor: !canConfirm ? '#ccc' : (danger ? '#ff5252' : undefined),
              cursor: !canConfirm ? 'not-allowed' : 'pointer',
              opacity: !canConfirm ? 0.6 : 1,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
